import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from 'app/store';
import { ApiResListsType, ApiResponseType } from 'global-services/api/OhsApiModels';
import { OhsGlobalSearchPayload } from 'search/OhsSearchModels';
import { TaskListsRecord } from 'task/OhsTaskTypes';
import OhsUserLocalServices from 'user/OhsUserLocalServices';
import { OhsUser } from 'user/OhsUser';
import handleRegisterAsyncData, {
  handleRegisterCountAsyncData,
} from 'global-components/register/OhsModuleRegisterUtils';

import getContractorLists, {
  getContractorAllocatedList,
  getContractorLinkedTasksList,
  getContractorSearchLists,
} from './OhsContractorService';
import {
  ContractorLinkedTaskListRes,
  OhsContractorFilterPayload,
  OhsContractorPayload,
} from './models/OhsContractorModels';
import OhsContractorRecord from './models/OhsContractorRecord';
import parseListAllocations from './utils/OhsParseListAllocations';

export interface ContractorListsRecordState {
  isLoading: boolean;
  contractorLists: ApiResListsType<any[]> | null;
  currentPage: number;
  linkedTasksList: {
    isLoading: boolean;
    taskList: TaskListsRecord[];
  };
}

const initialState: ContractorListsRecordState = {
  isLoading: true,
  contractorLists: null,
  currentPage: 1,
  linkedTasksList: {
    isLoading: false,
    taskList: [],
  },
};

export const getContractorListsWithAllocations = async (
  filterInfo: OhsContractorFilterPayload,
  searchFilterInfo: OhsGlobalSearchPayload,
  hasSearch: boolean
): Promise<ApiResListsType<any[]> | null> => {
  const contractorList = await (hasSearch
    ? getContractorSearchLists(searchFilterInfo)
    : getContractorLists(filterInfo));
  if (!contractorList || contractorList.items.length === 0) {
    return contractorList;
  }

  const getIDs = contractorList.items.map((item: OhsContractorRecord) => item._id);
  const allocatedList = await getContractorAllocatedList(getIDs);
  return parseListAllocations(contractorList, allocatedList) as ApiResListsType<
    OhsContractorRecord[]
  > | null;
};

export const getContractorListsWithoutAllocations = async (
  filterInfo: OhsContractorFilterPayload,
  searchFilterInfo: OhsGlobalSearchPayload,
  hasSearch: boolean
): Promise<ApiResListsType<any[]> | null> => {
  const contractorList = await (hasSearch
    ? getContractorSearchLists(searchFilterInfo)
    : getContractorLists(filterInfo));

  return contractorList;
};

const contractorListsRPC = async (
  user: OhsUser,
  filters: OhsContractorPayload,
  searchInfo: OhsGlobalSearchPayload,
  hasSearch: boolean
): Promise<any> => {
  const userTier = user?.tierNum ?? 0;
  const isView3 = filters?.viewPreset === 'view_3';

  // Tier 3 and view_3: use allocations logic
  if (userTier === 3 && isView3) {
    return getContractorListsWithAllocations(filters, searchInfo, hasSearch);
  }

  // For all other cases, use without allocations
  return getContractorListsWithoutAllocations(filters, searchInfo, hasSearch);
};

export const fetchContractorListsAsync = createAsyncThunk<
  ApiResListsType<OhsContractorRecord[]> | null,
  {
    contractorFilter: OhsContractorPayload;
    searchFilter: any;
    hasSearch: boolean;
  },
  { state: RootState }
>(
  'contractor/fetchContractorList',
  async ({ contractorFilter, searchFilter, hasSearch }, thunkAPI) => {
    const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
    try {
      return await contractorListsRPC(user, contractorFilter, searchFilter, hasSearch);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const fetchContractorListsCountAsync = createAsyncThunk<
  ApiResListsType<OhsContractorRecord[]> | null,
  {
    contractorFilter: any;
    searchFilter: OhsGlobalSearchPayload;
    hasSearch: boolean;
  },
  { state: RootState }
>(
  'contractor/fetchContractorListWithCount',
  async ({ contractorFilter, searchFilter, hasSearch }, thunkAPI) => {
    const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
    try {
      return await contractorListsRPC(user, contractorFilter, searchFilter, hasSearch);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const fetchContractorLinkedTasksListAsync = createAsyncThunk<
  ApiResponseType<ContractorLinkedTaskListRes> | null,
  string,
  { state: RootState }
>('contractor/fetchContractorLinkedTasksList', async (contractorId: string, thunkAPI) => {
  try {
    const response = await getContractorLinkedTasksList(contractorId);
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const contractorSlice = createSlice({
  name: 'contractor',
  initialState,
  reducers: {
    setCurrentRegisterPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setContractorIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContractorListsAsync.pending, (state) => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
        state.isLoading = true;
      })
      .addCase(fetchContractorListsAsync.fulfilled, (state, action) => {
        const contractorListRes = action.payload;
        if (contractorListRes) {
          const contractorLists = handleRegisterAsyncData(
            contractorListRes,
            'contractorListtotalPages'
          );
          state.contractorLists = contractorLists;
        }
      })
      .addCase(fetchContractorListsAsync.rejected, (state) => {
        state.contractorLists = null;
        state.isLoading = false;
      })
      .addCase(fetchContractorListsCountAsync.fulfilled, (state, action) => {
        const contractorListsCountRes = action.payload;
        if (contractorListsCountRes) {
          const countTaskLists = handleRegisterCountAsyncData(
            contractorListsCountRes,
            state.contractorLists,
            'contractorListtotalPages'
          );
          state.isLoading = false;
          state.contractorLists = countTaskLists;
        }
      })
      .addCase(fetchContractorLinkedTasksListAsync.pending, (state) => {
        state.linkedTasksList.taskList = [];
        state.linkedTasksList.isLoading = true;
      })
      .addCase(fetchContractorLinkedTasksListAsync.fulfilled, (state, action) => {
        if (action.payload?.success) {
          state.linkedTasksList.taskList = action.payload.result?.items ?? [];
          state.linkedTasksList.isLoading = false;
        }
      });
  },
});

const contractorState = (state: RootState) => state.contractor;
export const getOhsContractorModuleState = createSelector(
  [contractorState],
  (contractor) => contractor
);

export const { setCurrentRegisterPage, setContractorIsLoading } = contractorSlice.actions;
export const contractorModuleReducer = contractorSlice.reducer;
